import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { supabase } from '../../supabaseClient'
import Swal from 'sweetalert2'

const PAGE_SIZE = 10
const LEAVE_PAGE_SIZE = 10
const AUTO_FILL_WEEKS = 4   // how many weeks ahead to auto-fill on load
const pad = n => String(n).padStart(2, '0')
const JS_DAY_TO_KEY = { 1:'mon', 2:'tue', 3:'wed', 4:'thu', 5:'fri', 6:'sat', 0:'sun' }
const DAY_KEY_ORDER = ['mon','tue','wed','thu','fri','sat','sun']

const fmtDate = (d) =>
    new Date(d + 'T12:00:00').toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' })

const fmtTime = (iso) => {
    if (!iso) return '—'
    const d = new Date(iso)
    const h = d.getHours(), m = d.getMinutes()
    return `${pad(h % 12 || 12)}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`
}

// Format a raw "HH:MM" time string (from staff.default_scheduled_in) for display
const fmtRawTime = (t) => {
    if (!t) return '—'
    const [h, m] = t.split(':').map(Number)
    return `${pad(h % 12 || 12)}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`
}

const toLocalISODate = (date) => {
    const d = new Date(date)
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const getWeekDates = (anchor) => {
    const d = new Date(anchor + 'T12:00:00')
    const day = d.getDay()
    const monday = new Date(d)
    monday.setDate(d.getDate() - ((day + 6) % 7))
    return Array.from({ length: 7 }, (_, i) => {
        const dd = new Date(monday)
        dd.setDate(monday.getDate() + i)
        return toLocalISODate(dd)
    })
}

const getOffsetWeekDates = (anchor, weeksOffset) => {
    const d = new Date(anchor + 'T12:00:00')
    d.setDate(d.getDate() + weeksOffset * 7)
    return getWeekDates(toLocalISODate(d))
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const normalizeStaff = (s) => {
    const sched = s.staff_schedules?.[0]
    return {
        id: s.id, type: 'staff',
        first_name: s.first_name, last_name: s.last_name,
        role: s.role, employment_type: s.employment_type,
        work_days: sched?.work_days ?? ['mon','tue','wed','thu','fri'],
        default_scheduled_in: sched?.default_scheduled_in?.slice(0, 5) ?? null,
        default_scheduled_out: sched?.default_scheduled_out?.slice(0, 5) ?? null,
        is_recurring: sched?.is_recurring ?? false,
    }
}

const normalizeBarista = (b) => {
    const sched = b.staff_schedules?.[0]
    return {
        id: b.barista_id, type: 'barista',
        first_name: b.first_name, last_name: b.last_name,
        role: 'Barista', employment_type: b.employment_type ?? '',
        work_days: sched?.work_days ?? ['mon','tue','wed','thu','fri'],
        default_scheduled_in: sched?.default_scheduled_in?.slice(0, 5) ?? null,
        default_scheduled_out: sched?.default_scheduled_out?.slice(0, 5) ?? null,
        is_recurring: sched?.is_recurring ?? false,
    }
}

// ── Auto-fill recurring schedules ─────────────────────────────────────────
async function autoFillRecurringSchedules({ personList, branchId, weeksAhead = AUTO_FILL_WEEKS }) {
    const recurring = personList.filter(p => p.default_scheduled_in && p.default_scheduled_out && p.work_days?.length > 0)
    if (!recurring.length) return 0

    const todayStr = toLocalISODate(new Date())
    const allDates = []
    for (let w = 0; w < weeksAhead; w++) {
        getOffsetWeekDates(todayStr, w).forEach((date, i) => {
            if (date >= todayStr) allDates.push({ date, dayKey: DAY_KEY_ORDER[i] })
        })
    }
    const minDate = allDates[0]?.date
    const maxDate = allDates[allDates.length - 1]?.date
    if (!minDate) return 0

    const staffIds = recurring.filter(p => p.type === 'staff').map(p => p.id)
    const baristaIds = recurring.filter(p => p.type === 'barista').map(p => p.id)

    const existingQueries = []
    if (staffIds.length) existingQueries.push(supabase.from('staff_attendance').select('id, staff_id, date, clock_in, scheduled_in, scheduled_out').in('staff_id', staffIds).gte('date', minDate).lte('date', maxDate))
    if (baristaIds.length) existingQueries.push(supabase.from('staff_attendance').select('id, barista_id, date, clock_in, scheduled_in, scheduled_out').in('barista_id', baristaIds).gte('date', minDate).lte('date', maxDate))
    const existingResults = await Promise.all(existingQueries)

    const existingIndex = {}
    existingResults.forEach(({ data }) => (data ?? []).forEach(r => {
        const type = r.barista_id ? 'barista' : 'staff'
        const id = r.barista_id ?? r.staff_id
        existingIndex[`${type}:${id}|${r.date}`] = r
    }))

    const toTimestamp = (dateStr, timeStr) => {
        if (!dateStr || !timeStr) return null
        const d = new Date(`${dateStr}T${timeStr}:00`)
        return isNaN(d.getTime()) ? null : d.toISOString()
    }

    let created = 0
    for (const person of recurring) {
        for (const { date, dayKey } of allDates) {
            if (!person.work_days.includes(dayKey)) continue
            const key = `${person.type}:${person.id}|${date}`
            const existing = existingIndex[key]
            const schedIn = toTimestamp(date, person.default_scheduled_in)
            const schedOut = toTimestamp(date, person.default_scheduled_out)

            if (existing) {
                // Only refill blank rows — never touch one with a clock-in or times already set
                if (existing.clock_in || (existing.scheduled_in && existing.scheduled_out)) continue
                const { error } = await supabase.from('staff_attendance').update({ scheduled_in: schedIn, scheduled_out: schedOut }).eq('id', existing.id)
                if (!error) created++
                continue
            }

            const { error } = await supabase.rpc('upsert_staff_attendance_schedule', {
                p_branch_id: branchId,
                p_staff_id: person.type === 'staff' ? person.id : null,
                p_barista_id: person.type === 'barista' ? person.id : null,
                p_date: date,
                p_scheduled_in: schedIn,
                p_scheduled_out: schedOut,
                p_status: date >= toLocalISODate(new Date()) ? 'waiting' : 'absent',
            })
            if (!error) created++
            else console.error('Schedule upsert error:', error)
        }
    }
    return created
}

// Call this before inserting any on_leave record. Returns true if a leave
// record already exists for this staff member on the given date.
export async function checkDuplicateLeave(staffId, date) {
    const { data, error } = await supabase
        .from('staff_attendance')
        .select('id, status')
        .eq('staff_id', staffId)
        .eq('date', date)
        .maybeSingle()

    if (error) throw error
    if (!data) return false                    // no row at all — safe to insert
    if (data.status === 'on_leave') return true // already on leave that day
    return false                               // row exists but for another status
}

// ── Pagination ─────────────────────────────────────────────────────────────
function Pagination({ page, totalItems, pageSize, onPageChange }) {
    const totalPages = Math.ceil(totalItems / pageSize)
    if (totalPages <= 1) return null
    return (
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-gray-600">
                Showing {Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)} of {totalItems}
            </p>
            <div className="flex items-center gap-2">
                <button onClick={() => onPageChange(page - 1)} disabled={page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    ← Previous
                </button>
                {(() => {
                    const pages = []
                    const delta = 1
                    let start = Math.max(2, page - delta)
                    let end = Math.min(totalPages - 1, page + delta)
                    pages.push(1)
                    if (start > 2) pages.push('...')
                    for (let n = start; n <= end; n++) pages.push(n)
                    if (end < totalPages - 1) pages.push('...')
                    if (totalPages > 1) pages.push(totalPages)
                    return pages.map((n, i) =>
                        n === '...' ? (
                            <span key={`ellipsis-${i}`} className="px-2 py-2 text-sm text-gray-400">…</span>
                        ) : n === page ? (
                            <button key={n} onClick={() => onPageChange(n)}
                                className="px-4 py-2 bg-[#A0826D] text-white rounded-lg text-sm font-medium">{n}</button>
                        ) : (
                            <button key={n} onClick={() => onPageChange(n)}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">{n}</button>
                        )
                    )
                })()}
                <button onClick={() => onPageChange(page + 1)} disabled={page === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    Next →
                </button>
            </div>
        </div>
    )
}

// ── Leave History Section ──────────────────────────────────────────────────
function LeaveHistorySection({ branchId, staffList }) {
    const [leaveRecords, setLeaveRecords] = useState([])
    const [loading, setLoading]           = useState(true)
    const [leavePage, setLeavePage]       = useState(1)
    const [searchQ, setSearchQ]           = useState('')

    useEffect(() => {
        if (!branchId || !staffList.length) return
        fetchLeave()
    }, [branchId, staffList])

    const fetchLeave = async () => {
        setLoading(true)
        const staffIds = staffList.map(s => s.id)
        const todayStr = toLocalISODate(new Date())
        const { data } = await supabase
            .from('staff_attendance')
            .select('*')
            .in('staff_id', staffIds)
            .eq('status', 'on_leave')
            .lte('date', todayStr)
            .order('date', { ascending: false })
        setLeaveRecords(data ?? [])
        setLoading(false)
    }

    const staffMap = useMemo(() =>
        Object.fromEntries(staffList.map(s => [s.id, s])),
    [staffList])

    const filtered = useMemo(() => {
        const q = searchQ.trim().toLowerCase()
        if (!q) return leaveRecords
        return leaveRecords.filter(r => {
            const s = staffMap[r.staff_id]
            if (!s) return false
            return `${s.first_name} ${s.last_name}`.toLowerCase().includes(q) ||
                   (r.notes ?? '').toLowerCase().includes(q)
        })
    }, [leaveRecords, searchQ, staffMap])

    useEffect(() => { setLeavePage(1) }, [searchQ])

    const paged = useMemo(() => {
        const start = (leavePage - 1) * LEAVE_PAGE_SIZE
        return filtered.slice(start, start + LEAVE_PAGE_SIZE)
    }, [filtered, leavePage])

    return (
        <div className="mt-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                    <h3 className="text-sm font-bold text-gray-800 m-0">Leave History</h3>
                    <p className="text-xs text-gray-400 mt-0.5">All recorded on-leave days for this branch</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                            width="13" height="13" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                        </svg>
                        <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
                            placeholder="Search staff or reason…"
                            className="pl-7 pr-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                    </div>
                    <button onClick={fetchLeave}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50 transition-colors">↺</button>
                </div>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-xs min-w-[500px]">
                        <thead>
                            <tr className="bg-gray-50">
                                {['Staff Member', 'Date', 'Reason / Notes'].map(h => (
                                    <th key={h} className="px-4 py-3 text-left font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 text-[11px]">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={3} className="py-10 text-center text-xs text-gray-400">
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#A0826D]" />
                                        Loading leave history…
                                    </div>
                                </td></tr>
                            ) : paged.length === 0 ? (
                                <tr><td colSpan={3} className="py-10 text-center text-xs text-gray-400">
                                    {filtered.length === 0 && leaveRecords.length > 0
                                        ? 'No records match your search.'
                                        : 'No leave records found for this branch.'}
                                </td></tr>
                            ) : paged.map((r, idx) => {
                                const staff = staffMap[r.staff_id]
                                return (
                                    <tr key={r.id} className={`border-b border-gray-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
                                        <td className="px-4 py-2.5">
                                            {staff ? (
                                                <div>
                                                    <p className="font-semibold text-gray-800 m-0">{staff.first_name} {staff.last_name}</p>
                                                    <p className="text-[10px] text-gray-400 m-0">{staff.role}</p>
                                                </div>
                                            ) : <span className="text-gray-400">Unknown</span>}
                                        </td>
                                        <td className="px-4 py-2.5 font-medium text-gray-700">{fmtDate(r.date)}</td>
                                        <td className="px-4 py-2.5 text-gray-500">
                                            {r.notes
                                                ? <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 text-[10px] font-medium">{r.notes}</span>
                                                : <span className="text-gray-300">—</span>}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
                <Pagination page={leavePage} totalItems={filtered.length} pageSize={LEAVE_PAGE_SIZE} onPageChange={setLeavePage} />
            </div>
            {!loading && (
                <p className="text-xs text-gray-400 mt-2 text-right">
                    {filtered.length} leave record{filtered.length !== 1 ? 's' : ''}{searchQ ? ' matching your search' : ''}
                </p>
            )}
        </div>
    )
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function ShiftManagement({ branchId: initialBranchId, branches = [] }) {
    const [activeBranchId, setActiveBranchId] = useState(initialBranchId)
    const [staffList, setStaffList] = useState([])
    const [baristaList, setBaristaList] = useState([])
    const [attendanceMap, setAttendanceMap]= useState({})
    const [todayAttMap, setTodayAttMap] = useState({})
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [generating, setGenerating] = useState(false)
    const [weekAnchor, setWeekAnchor] = useState(toLocalISODate(new Date()))
    const [shiftModal, setShiftModal] = useState(null)
    const [shiftForm, setShiftForm] = useState({
        scheduled_in: '', scheduled_out: '', workDays: [],
        saveAsDefault: false,
    })
    const [initialShiftForm, setInitialShiftForm] = useState(null)
    const [page, setPage]                     = useState(1)
    const [searchQ, setSearchQ]               = useState('')
    const [roleFilter, setRoleFilter]         = useState('all')

    const weekDates = useMemo(() => getWeekDates(weekAnchor), [weekAnchor])
    const todayStr  = toLocalISODate(new Date())

    useEffect(() => { setActiveBranchId(initialBranchId); setPage(1) }, [initialBranchId])
    useEffect(() => { if (activeBranchId) fetchAll() }, [activeBranchId, weekDates])

    const fetchAll = async (skipAutoFill = false) => {
        setLoading(true)
        setPage(1)

        // ── Staff (excludes Barista-role rows — those live in barista_info) ──
        const { data: staffData } = await supabase
            .from('staff')
            .select('id, first_name, last_name, role, status, employment_type, staff_schedules(work_days, default_scheduled_in, default_scheduled_out, is_recurring)')
            .eq('branch_id', activeBranchId)
            .eq('staff_schedules.branch_id', activeBranchId)
            .eq('status', 'Active')
            .neq('role', 'Barista')
            .order('first_name')

        // ── Baristas ──────────────────────────────────────────────────────────
        console.log('Fetching baristas for branch:', activeBranchId)
        const { data: baristaData, error: baristaError } = await supabase
            .from('barista_info')
            .select('barista_id, first_name, last_name, status, employment_type, staff_schedules(work_days, default_scheduled_in, default_scheduled_out, is_recurring)')
            .eq('branch_id', activeBranchId)
            .eq('staff_schedules.branch_id', activeBranchId)
            .eq('status', 'Active')
            .order('first_name')

        if (baristaError) console.error('Barista fetch error:', baristaError)
        console.log('Barista rows returned:', baristaData?.length ?? 0, baristaData)

        if (baristaError) console.error('Barista fetch error:', baristaError)

        if (!staffData?.length && !baristaData?.length) {
            setStaffList([]); setBaristaList([]); setLoading(false); return
        }

        const flatStaff = (staffData   ?? []).map(normalizeStaff)
        const flatBarista = (baristaData ?? []).map(normalizeBarista)
        const flatAll = [...flatStaff, ...flatBarista]

        if (!skipAutoFill) {
            const todayD = new Date(todayStr + 'T12:00:00')
            const viewedEndD = new Date(weekDates[6] + 'T12:00:00')
            const weeksUntilViewed = Math.max(0, Math.ceil((viewedEndD - todayD) / (7 * 24 * 60 * 60 * 1000))) + 1
            const weeksAhead = Math.min(52, Math.max(AUTO_FILL_WEEKS, weeksUntilViewed))

            await autoFillRecurringSchedules({ personList: flatAll, branchId: activeBranchId, weeksAhead })
        }

        const staffIds   = flatStaff.map(p => p.id)
        const baristaIds = flatBarista.map(p => p.id)

        // ── Attendance for the visible week ─────────────────────────────────
        const attQueries = []
        if (staffIds.length)   attQueries.push(supabase.from('staff_attendance').select('*').in('staff_id', staffIds).gte('date', weekDates[0]).lte('date', weekDates[6]))
        if (baristaIds.length) attQueries.push(supabase.from('staff_attendance').select('*').in('barista_id', baristaIds).gte('date', weekDates[0]).lte('date', weekDates[6]))
        const attResults = await Promise.all(attQueries)

        const aMap = {}
        attResults.forEach(({ data }) => (data ?? []).forEach(r => {
            const type = r.barista_id ? 'barista' : 'staff'
            const id   = r.barista_id ?? r.staff_id
            aMap[`${type}:${id}|${r.date}`] = r
        }))
        setAttendanceMap(aMap)

        // ── Today's status ───────────────────────────────────────────────────
        const todayQueries = []
        if (staffIds.length)   todayQueries.push(supabase.from('staff_attendance').select('staff_id, barista_id, clock_in, clock_out, status, scheduled_out').in('staff_id', staffIds).eq('date', todayStr))
        if (baristaIds.length) todayQueries.push(supabase.from('staff_attendance').select('staff_id, barista_id, clock_in, clock_out, status, scheduled_out').in('barista_id', baristaIds).eq('date', todayStr))
        const todayResults = await Promise.all(todayQueries)

        const tMap = {}
        todayResults.forEach(({ data }) => (data ?? []).forEach(r => {
            const type = r.barista_id ? 'barista' : 'staff'
            const id   = r.barista_id ?? r.staff_id
            tMap[`${type}:${id}`] = r
        }))
        setTodayAttMap(tMap)

        setStaffList(flatStaff)
        setBaristaList(flatBarista)
        setLoading(false)
    }

    // ── Manual "Generate Schedules" button ───────────────────────────────
    const handleGenerateSchedules = async () => {
        if (!staffList.length) return
        const recurringCount = [...staffList, ...baristaList].filter(
            p => p.default_scheduled_in && p.default_scheduled_out
        ).length
        if (recurringCount === 0) {
            Swal.fire({
                icon: 'info',
                title: 'No Recurring Schedules',
                text: "No staff have a default recurring schedule set. Open a staff member's schedule and enable 'Save as recurring default' to set one.",
                confirmButtonColor: '#A0826D',
            })
            return
        }

        setGenerating(true)
        try {
            const created = await autoFillRecurringSchedules({
                personList: [...staffList, ...baristaList],
                branchId: activeBranchId,
                weeksAhead: AUTO_FILL_WEEKS,
            })
            await fetchAll(true)
            Swal.fire({
                icon: 'success',
                title: 'Schedules Generated',
                text: created > 0
                    ? `Created ${created} new shift row${created !== 1 ? 's' : ''} for ${recurringCount} staff member${recurringCount !== 1 ? 's' : ''} (${AUTO_FILL_WEEKS} weeks ahead).`
                    : `All shift rows are already up to date for the next ${AUTO_FILL_WEEKS} weeks.`,
                confirmButtonColor: '#A0826D',
                timer: 3000,
                showConfirmButton: false,
            })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setGenerating(false)
        }
    }

    const allPeople = useMemo(() => [...staffList, ...baristaList], [staffList, baristaList])

    const roleOptions = useMemo(() => {
        const roles = [...new Set(allPeople.map(s => s.role).filter(Boolean))].sort()
        return roles
    }, [allPeople])

    const filteredStaff = useMemo(() => {
        const q = searchQ.trim().toLowerCase()
        return allPeople.filter(s => {
            const matchSearch = !q ||
                `${s.first_name} ${s.last_name}`.toLowerCase().includes(q) ||
                s.role.toLowerCase().includes(q)
            const matchRole = roleFilter === 'all' || s.role === roleFilter
            return matchSearch && matchRole
        })
    }, [allPeople, searchQ, roleFilter])

    useEffect(() => { setPage(1) }, [searchQ, roleFilter])

    useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
        console.log('SESSION CHECK:', session)
    })
}, [])

    const pagedStaff = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE
        return filteredStaff.slice(start, start + PAGE_SIZE)
    }, [filteredStaff, page])

    // ── Open combined modal ───────────────────────────────────────────────
    const openShiftModal = (staff) => {
        const workDays = staff.work_days?.length > 0
            ? staff.work_days
            : ['mon','tue','wed','thu','fri']

        const existing = weekDates
            .map(d => attendanceMap[`${staff.type}:${staff.id}|${d}`])
            .find(r => r?.scheduled_in)

        const defaultIn  = staff.default_scheduled_in  ? staff.default_scheduled_in.slice(0, 5)  : ''
        const defaultOut = staff.default_scheduled_out ? staff.default_scheduled_out.slice(0, 5) : ''
        const existingIn = existing?.scheduled_in
            ? new Date(existing.scheduled_in).toTimeString().slice(0, 5) : ''
        const existingOut = existing?.scheduled_out
            ? new Date(existing.scheduled_out).toTimeString().slice(0, 5) : ''

        const form = {
            workDays,
            scheduled_in: defaultIn || existingIn,
            scheduled_out: defaultOut || existingOut,
            saveAsDefault: staff.is_recurring === true,
        }
        setShiftForm(form)
        setInitialShiftForm(form)
        setShiftModal(staff)
    }

    const closeModal = () => { setShiftModal(null); setInitialShiftForm(null) }

    // ── Save combined modal ───────────────────────────────────────────────
    const handleSave = async () => {
        if (shiftForm.saveAsDefault && (!shiftForm.scheduled_in || !shiftForm.scheduled_out)) {
            Swal.fire({
                icon: 'warning', title: 'Missing Times',
                text: 'Please set both a shift start and end time.',
                confirmButtonColor: '#A0826D',
            })
            return
        }

        setSaving(true)

    const person = shiftModal
    const isBarista = person.type === 'barista'
    const prevWorkDays = person.work_days ?? ['mon','tue','wed','thu','fri']
    const newWorkDays  = shiftForm.workDays
    const removedDays  = prevWorkDays.filter(d => !newWorkDays.includes(d))

    try {
        let schedPayload = {
            branch_id: activeBranchId,
            work_days: newWorkDays,
            is_recurring: shiftForm.saveAsDefault,
        }
        schedPayload[isBarista ? 'barista_id' : 'staff_id'] = person.id

            if (shiftForm.saveAsDefault) {
                schedPayload.default_scheduled_in  = shiftForm.scheduled_in
                schedPayload.default_scheduled_out = shiftForm.scheduled_out
            } else {
                schedPayload.default_scheduled_in  = null
                schedPayload.default_scheduled_out = null
            }

            const { error: schedError } = await supabase
                .from('staff_schedules')
                .upsert(schedPayload, { onConflict: isBarista ? 'barista_id,branch_id' : 'staff_id,branch_id' })
            if (schedError) throw schedError

            // ── Nullify removed days ──────────────────────────────────────
            if (removedDays.length > 0) {
                const { data: removedRows } = await supabase
                    .from('staff_attendance')
                    .select('id, date')
                    .eq(isBarista ? 'barista_id' : 'staff_id', person.id)
                    .gte('date', todayStr)
                    .is('clock_in', null)

                const idsToNullify = (removedRows ?? [])
                    .filter(r => {
                        const key = JS_DAY_TO_KEY[new Date(r.date + 'T12:00:00').getDay()]
                        return removedDays.includes(key)
                    })
                    .map(r => r.id)

                if (idsToNullify.length > 0) {
                    const { error } = await supabase
                        .from('staff_attendance')
                        .update({ scheduled_in: null, scheduled_out: null })
                        .in('id', idsToNullify)
                    if (error) throw error
                }
            }

            // ── Upsert this week's attendance rows so the grid updates immediately ──
            if (shiftForm.scheduled_in?.trim() && shiftForm.scheduled_out?.trim()) {
                const toTimestamp = (dateStr, timeStr) => {
                    if (!timeStr?.trim()) return null
                    const d = new Date(`${dateStr}T${timeStr}:00`)
                    return isNaN(d.getTime()) ? null : d.toISOString()
                }

                const updates = []
                const inserts = []

                for (let i = 0; i < weekDates.length; i++) {
                    const date   = weekDates[i]
                    const dayKey = DAY_KEY_ORDER[i]
                    if (!newWorkDays.includes(dayKey)) continue

                    const key      = `${person.type}:${person.id}|${date}`
                    const existing = attendanceMap[key]
                    if (existing?.clock_in) continue

                    const schedIn  = toTimestamp(date, shiftForm.scheduled_in)
                    const schedOut = toTimestamp(date, shiftForm.scheduled_out)
                    if (!schedIn || !schedOut) continue

                    if (existing?.id) {
                        updates.push({
                            id: existing.id,
                            branch_id: activeBranchId,
                            scheduled_in: schedIn,
                            scheduled_out: schedOut,
                            status: date >= toLocalISODate(new Date()) ? 'waiting' : 'absent',
                            [isBarista ? 'barista_id' : 'staff_id']: person.id,
                        })
                    } else {
                        inserts.push({
                            branch_id: activeBranchId, date,
                            status: date >= toLocalISODate(new Date()) ? 'waiting' : 'absent',
                            scheduled_in: schedIn, scheduled_out: schedOut,
                            [isBarista ? 'barista_id' : 'staff_id']: person.id,
                        })
                    }
                }

                if (updates.length > 0) {
                    const updatePromises = updates.map(u =>
                        supabase
                            .from('staff_attendance')
                            .update({
                                scheduled_in: u.scheduled_in,
                                scheduled_out: u.scheduled_out,
                                status: u.status,
                            })
                            .eq('id', u.id)
                    )
                    const updateResults = await Promise.all(updatePromises)
                    const updError = updateResults.find(r => r.error)?.error
                    if (updError) throw updError
                }

                if (inserts.length > 0) {
                    for (const row of inserts) {
                        const { error } = await supabase.rpc('upsert_staff_attendance_schedule', {
                            p_branch_id: row.branch_id,
                            p_staff_id: row.staff_id ?? null,
                            p_barista_id: row.barista_id ?? null,
                            p_date: row.date,
                            p_scheduled_in: row.scheduled_in,
                            p_scheduled_out: row.scheduled_out,
                            p_status: row.status,
                        })
                        if (error) throw error
                    }
                }
            }

            closeModal()
            await fetchAll(true)

            const parts = []
            if (shiftForm.saveAsDefault) {
                parts.push('recurring default saved')
            } else {
                parts.push('recurring schedule cleared')
            }

            Swal.fire({
                icon: 'success', title: 'Schedule Saved',
                text: `${person.first_name}'s schedule has been updated${parts.length ? ` (${parts.join(', ')})` : ''}.`,
                confirmButtonColor: '#A0826D', timer: 2500, showConfirmButton: false,
            })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setSaving(false)
        }
    }

    const toggleWorkDay = (dayKey) => {
        setShiftForm(f => {
            const newWorkDays = f.workDays.includes(dayKey)
                ? f.workDays.filter(d => d !== dayKey)
                : [...f.workDays, dayKey]
            return { ...f, workDays: newWorkDays }
        })
    }

    const prevWeek = () => {
        const d = new Date(weekAnchor + 'T12:00:00'); d.setDate(d.getDate() - 7)
        setWeekAnchor(toLocalISODate(d))
    }
    const nextWeek = () => {
        const d = new Date(weekAnchor + 'T12:00:00'); d.setDate(d.getDate() + 7)
        setWeekAnchor(toLocalISODate(d))
    }

    const hasChanges = useMemo(() => {
        if (!initialShiftForm) return false
        return (
            shiftForm.scheduled_in  !== initialShiftForm.scheduled_in  ||
            shiftForm.scheduled_out !== initialShiftForm.scheduled_out ||
            shiftForm.workDays.slice().sort().join(',')  !== initialShiftForm.workDays.slice().sort().join(',') ||
            shiftForm.saveAsDefault !== initialShiftForm.saveAsDefault ||
            shiftForm.clearDefault  !== initialShiftForm.clearDefault
        )
    }, [shiftForm, initialShiftForm])

    const getInStoreStatus = (staffId) => {
        const rec = todayAttMap[staffId]
        if (!rec) return 'absent'
        if (rec.status === 'on_leave') return 'on_leave'
        if (rec.status === 'waiting') return 'waiting'   // ← add this
        if (rec.clock_in && !rec.clock_out) return 'present'
        if (rec.clock_in && rec.clock_out) {
            const scheduledOut = rec.scheduled_out ? new Date(rec.scheduled_out) : null
            const clockedOut   = new Date(rec.clock_out)
            if (!scheduledOut || clockedOut >= scheduledOut) return 'done'
            return 'early_out'
        }
        return 'absent'
    }

    const InStoreChip = ({ staffId }) => {
        const s = getInStoreStatus(staffId)
        const cfg = {
            present:   { label: 'In Store',   bg: '#F0FDF4', color: '#16A34A' },
            done:      { label: 'Shift Done',  bg: '#EFF6FF', color: '#2563EB' },
            early_out: { label: 'Early Out',   bg: '#FFF7ED', color: '#D97706' },
            on_leave:  { label: 'On Leave',    bg: '#F5F3FF', color: '#7C3AED' },
            waiting:   { label: 'Waiting',     bg: '#FFFBEB', color: '#D97706' },
            absent:    { label: 'Not In',      bg: '#F9FAFB', color: '#9CA3AF' },
        }[s] ?? { label: '—', bg: '#F9FAFB', color: '#9CA3AF' }
        return (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap"
                style={{ background: cfg.bg, color: cfg.color }}>
                {cfg.label}
            </span>
        )
    }

    const recurringCount = useMemo(() =>
        allPeople.filter(s => s.default_scheduled_in && s.default_scheduled_out).length,
    [allPeople])

    return (
        <div className="p-3 sm:p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <div>
                        <h2 className="text-lg font-bold text-gray-800 m-0">Shift Management</h2>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {fmtDate(weekDates[0])} — {fmtDate(weekDates[6])}
                        </p>
                    </div>
                    <button
                        onClick={handleGenerateSchedules}
                        disabled={generating || loading}
                        title={`Auto-fill recurring schedules for next ${AUTO_FILL_WEEKS} weeks`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#A0826D] text-sm text-[#A0826D] hover:bg-[#A0826D]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium">
                        {generating ? (
                            <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3"/>
                                <path fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                            </svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                                <path d="M21 3v5h-5"/>
                                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                                <path d="M8 16H3v5"/>
                            </svg>
                        )}
                        Generate Schedules
                        {recurringCount > 0 && (
                            <span className="ml-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-[#A0826D]/15 font-semibold">
                                {recurringCount}
                            </span>
                        )}
                    </button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {branches.length > 0 && (
                        <select value={activeBranchId ?? ''}
                            onChange={e => { setActiveBranchId(e.target.value); setPage(1) }}
                            className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#A0826D]">
                            <option value="" disabled>Select branch…</option>
                            {branches.map(b => (
                                <option key={b.branch_id} value={b.branch_id}>{b.name}</option>
                            ))}
                        </select>
                    )}
                    <div className="flex items-center gap-1">
                        <button onClick={prevWeek}
                            className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">← Last Week</button>
                        <button onClick={() => setWeekAnchor(toLocalISODate(new Date()))}
                            className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 transition-colors">Today</button>
                        <button onClick={nextWeek}
                            className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">Next Week →</button>
                    </div>

                    <button onClick={() => fetchAll()}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors">↺ Refresh</button>
                </div>
            </div>

            {/* Search + Role filter */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                        width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
                        placeholder="Search by name or role…"
                        className="w-full pl-8 pr-4 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                </div>
                <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#A0826D]">
                    <option value="all">All roles</option>
                    {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <p className="text-xs text-gray-400 whitespace-nowrap">
                    {filteredStaff.length} staff member{filteredStaff.length !== 1 ? 's' : ''}
                </p>
                {(searchQ || roleFilter !== 'all') && (
                    <button onClick={() => { setSearchQ(''); setRoleFilter('all') }}
                        className="text-xs text-[#A0826D] hover:underline whitespace-nowrap">Clear filters</button>
                )}
            </div>

            {/* Grid */}
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse min-w-[900px]">
                        <thead>
                            <tr className="bg-gray-50">
                                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-52 border-b border-gray-200">
                                    Staff Member
                                </th>
                                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 w-24">
                                    Today
                                </th>
                                {weekDates.map((date, i) => (
                                    <th key={date}
                                        className={`px-2 py-3 text-center text-xs font-semibold uppercase tracking-wider border-b border-gray-200 ${
                                            date === todayStr ? 'text-[#A0826D] bg-amber-50' : 'text-gray-500'
                                        }`}>
                                        <div>{DAY_LABELS[i]}</div>
                                        <div className={`text-base font-bold mt-0.5 ${date === todayStr ? 'text-[#A0826D]' : 'text-gray-700'}`}>
                                            {new Date(date + 'T12:00:00').getDate()}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={10} className="py-16 text-center text-sm text-gray-400">
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#A0826D]" />
                                            Loading shifts…
                                        </div>
                                    </td>
                                </tr>
                            ) : pagedStaff.length === 0 ? (
                                <tr>
                                    <td colSpan={10} className="py-12 text-center text-sm text-gray-400">
                                        {filteredStaff.length === 0 && staffList.length > 0
                                            ? 'No staff match your search or filter.'
                                            : 'No active staff in this branch.'}
                                    </td>
                                </tr>
                            ) : pagedStaff.map((staff, idx) => (
                                <tr key={`${staff.type}-${staff.id}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}>

                                    {/* Staff cell */}
                                    <td className="px-4 py-3 border-b border-gray-100">
                                        <div className="w-full">
                                            <div className="flex items-center gap-1.5">
                                                <p className="font-semibold text-sm text-gray-800 m-0 truncate max-w-[140px]">
                                                    {staff.first_name} {staff.last_name}
                                                </p>
                                                {staff.default_scheduled_in && staff.default_scheduled_out && (
                                                    <span
                                                        title={`Recurring: ${fmtRawTime(staff.default_scheduled_in)} – ${fmtRawTime(staff.default_scheduled_out)}`}
                                                        className="flex-shrink-0 text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                                                        style={{ background: '#F0FDF4', color: '#16A34A' }}>
                                                        ↻
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-400 m-0">{staff.role}</p>
                                            <div className="flex gap-0.5 mt-1 flex-wrap">
                                                {DAY_KEY_ORDER.map((d, i) => (
                                                    <span key={d} className="text-[9px] px-1 py-0.5 rounded font-semibold"
                                                        style={{
                                                            background: (staff.work_days ?? []).includes(d) ? '#A0826D22' : '#F3F4F6',
                                                            color:      (staff.work_days ?? []).includes(d) ? '#A0826D'   : '#D1D5DB',
                                                        }}>
                                                        {DAY_LABELS[i]}
                                                    </span>
                                                ))}
                                            </div>
                                            {staff.default_scheduled_in && staff.default_scheduled_out && (
                                                <p className="text-[9px] text-green-600 mt-0.5 font-medium">
                                                    ↻ {fmtRawTime(staff.default_scheduled_in)} – {fmtRawTime(staff.default_scheduled_out)}
                                                </p>
                                            )}
                                        </div>
                                        <button onClick={() => openShiftModal(staff)}
                                            className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-lg border border-dashed border-[#A0826D] text-[#A0826D] hover:bg-[#A0826D]/10 transition-colors">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
                                                fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"/>
                                                <polyline points="12 6 12 12 16 14"/>
                                            </svg>
                                            Set Schedule
                                        </button>
                                    </td>

                                    {/* Today status */}
                                    <td className="px-3 py-3 text-center border-b border-gray-100">
                                        <InStoreChip staffId={`${staff.type}:${staff.id}`} />
                                    </td>

                                    {/* Week cells */}
                                    {weekDates.map((date, di) => {
                                    const key = `${staff.type}:${staff.id}|${date}`
                                    const rec = attendanceMap[key]
                                    const isOnLeave = rec?.status === 'on_leave'
                                    const isFuture = date > todayStr
                                    const workDays = staff.work_days ?? []
                                    const isNonWorkDay = workDays.length > 0 && !workDays.includes(DAY_KEY_ORDER[di])

                                    // Prefer the real attendance row; fall back to the recurring default
                                    // so the admin sees the intended schedule even before a row exists for this date.
                                    const hasActualShift  = !!(rec?.scheduled_in && rec?.scheduled_out)
                                    const hasDefaultShift = !!(staff.default_scheduled_in && staff.default_scheduled_out)
                                    const hasShift = hasActualShift || (!isNonWorkDay && hasDefaultShift)

                                    const displayIn  = hasActualShift ? fmtTime(rec.scheduled_in)  : fmtRawTime(staff.default_scheduled_in)
                                    const displayOut = hasActualShift ? fmtTime(rec.scheduled_out) : fmtRawTime(staff.default_scheduled_out)

                                    return (
                                            <td key={date}
                                                className={`px-1 py-2 text-center border-b border-gray-100 ${
                                                    date === todayStr ? 'bg-amber-50 border-l border-r border-amber-200' : ''
                                                }`}>
                                                <button
                                                    onClick={() => !isNonWorkDay && openShiftModal(staff)}
                                                    title={isNonWorkDay ? 'Not a work day' : `Edit ${staff.first_name}'s shift on ${fmtDate(date)}`}
                                                    className={`w-full min-h-[52px] rounded-xl p-1.5 text-[10px] font-semibold border border-transparent transition-all flex flex-col items-center justify-center gap-0.5 ${
                                                        (isNonWorkDay || isOnLeave) ? 'cursor-default' : 'hover:border-[#A0826D] hover:shadow-sm'
                                                    }`}
                                                    style={{
                                                        background: isNonWorkDay ? '#F3F4F6'
                                                                : isOnLeave   ? '#F5F3FF'
                                                                : hasShift    ? '#F0FDF4'
                                                                : isFuture    ? '#F9FAFB' : '#FFF9F7',
                                                        color:      isNonWorkDay ? '#D1D5DB'
                                                                : isOnLeave   ? '#7C3AED'
                                                                : hasShift    ? '#16A34A'
                                                                : isFuture    ? '#9CA3AF' : '#A0826D',
                                                        pointerEvents: (isNonWorkDay || isOnLeave) ? 'none' : 'auto',
                                                    }}>
                                                    {isOnLeave ? (
                                                        <span className="text-[9px]">On Leave</span>
                                                    ) : !isNonWorkDay && hasShift ? (
                                                        <>
                                                            <span>{displayIn}</span>
                                                            <span className="opacity-60">–</span>
                                                            <span>{displayOut}</span>
                                                        </>
                                                    ) : isNonWorkDay ? (
                                                        <span className="text-[9px]">Off</span>
                                                    ) : (
                                                        <span>Unset</span>
                                                    )}
                                                </button>
                                            </td>
                                        )
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Pagination page={page} totalItems={filteredStaff.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
            </div>

            {/* Legend */}
            <div className="flex gap-4 mt-3 flex-wrap items-center">
                <div className="flex items-center gap-1.5 text-xs text-green-600">
                    <div className="w-3 h-3 rounded bg-green-50 border border-green-200" />
                    Shift set
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <div className="w-3 h-3 rounded bg-gray-100 border border-gray-300" />
                    Off / Unset
                </div>
                <div className="flex items-center gap-1.5 text-xs text-purple-600">
                    <div className="w-3 h-3 rounded bg-purple-50 border border-purple-200" />
                    On Leave
                </div>
                <div className="flex items-center gap-1.5 text-xs text-green-600">
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold bg-green-50 text-green-600">↻</span>
                    Recurring schedule
                </div>
                <div className="flex items-center gap-1.5 text-xs text-green-600">
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-green-50 text-green-700">In Store</span>
                    Currently clocked in
                </div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-gray-100 text-gray-500">Not In</span>
                    Not clocked in today
                </div>
                <p className="text-xs text-gray-400 ml-auto">Click a cell or "Set Schedule" to manage</p>
            </div>

            {/* Leave History */}
            {!loading && staffList.length > 0 && (
                <LeaveHistorySection branchId={activeBranchId} staffList={staffList} />
            )}

            {/* Combined Schedule Modal */}
            {shiftModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black bg-opacity-40"
                        onClick={() => !saving && closeModal()} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">

                        <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                            <h3 className="font-bold text-gray-800 text-base m-0">
                                Set Schedule — {shiftModal.first_name} {shiftModal.last_name}
                            </h3>
                            <p className="text-sm text-gray-500 m-0 mt-0.5">
                                {fmtDate(weekDates[0])} to {fmtDate(weekDates[6])}
                            </p>
                        </div>

                        <div className="px-6 py-5 space-y-6">

                            {/* ── Work Days ── */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Work Days</label>
                                <p className="text-xs text-gray-400 mb-3">
                                    Which days does this staff member regularly work? Days turned off will have their scheduled times cleared.
                                </p>
                                <div className="flex gap-1.5 flex-wrap">
                                    {DAY_KEY_ORDER.map((dayKey, i) => {
                                        const isWorkDay = shiftForm.workDays.includes(dayKey)
                                        return (
                                            <button key={dayKey} onClick={() => toggleWorkDay(dayKey)}
                                                className={`flex flex-col items-center px-3 py-2 rounded-xl border-2 text-[11px] font-semibold transition-all ${
                                                    isWorkDay
                                                        ? 'border-[#A0826D] bg-[#A0826D]/10 text-[#A0826D]'
                                                        : 'border-gray-200 bg-gray-50 text-gray-400 hover:border-gray-300'
                                                }`}>
                                                {DAY_LABELS[i]}
                                            </button>
                                        )
                                    })}
                                </div>
                                <div className="flex gap-2 mt-2">
                                    <button onClick={() => setShiftForm(f => {
                                        const wd = ['mon','tue','wed','thu','fri']
                                        return { ...f, workDays: wd }
                                    })} className="text-[11px] text-[#A0826D] hover:underline">Weekdays</button>
                                    <span className="text-gray-300 text-[11px]">·</span>
                                    <button onClick={() => setShiftForm(f => ({ ...f, workDays: [...DAY_KEY_ORDER] }))}
                                        className="text-[11px] text-[#A0826D] hover:underline">All days</button>
                                    <span className="text-gray-300 text-[11px]">·</span>
                                    <button onClick={() => setShiftForm(f => ({ ...f, workDays: [] }))}
                                        className="text-[11px] text-gray-400 hover:underline">Clear</button>
                                </div>
                            </div>

                            {/* ── Shift Times ── */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-3">Shift Hours</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Shift Start</label>
                                        <input type="time" value={shiftForm.scheduled_in}
                                            onChange={e => setShiftForm(f => ({ ...f, scheduled_in: e.target.value }))}
                                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Shift End</label>
                                        <input type="time" value={shiftForm.scheduled_out}
                                            onChange={e => setShiftForm(f => ({ ...f, scheduled_out: e.target.value }))}
                                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                                    </div>
                                </div>
                                {shiftForm.scheduled_in && shiftForm.scheduled_out && (() => {
                                    const [ih, im] = shiftForm.scheduled_in.split(':').map(Number)
                                    const [oh, om] = shiftForm.scheduled_out.split(':').map(Number)
                                    const totalMins = (oh * 60 + om) - (ih * 60 + im)
                                    if (totalMins <= 0) return null
                                    const h = Math.floor(totalMins / 60), m = totalMins % 60
                                    return (
                                        <p className="text-xs text-[#A0826D] font-semibold mt-2">
                                            Total Shift Time: {h > 0 ? `${h}h ` : ''}{m > 0 ? `${m}m` : ''} shift
                                        </p>
                                    )
                                })()}
                            </div>

                            {/* ── Recurring Default ── */}
                            <div className="rounded-xl border-2 border-green-100 bg-green-50/40 px-4 py-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-gray-700 m-0">Recurring Schedule</p>
                                        <p className="text-xs text-gray-500 mt-0.5">
                                            Save these hours as a permanent default. Future weeks will be auto-filled on every page load.
                                        </p>
                                        {shiftModal.default_scheduled_in && shiftModal.default_scheduled_out && !shiftForm.clearDefault && (
                                            <p className="text-xs text-green-600 font-semibold mt-1.5">
                                                Current: {fmtRawTime(shiftModal.default_scheduled_in)} – {fmtRawTime(shiftModal.default_scheduled_out)}
                                            </p>
                                        )}
                                    </div>
                                    {/* ── FIX: toggle reads shiftForm.saveAsDefault, not shiftForm.clearDefault ── */}
                                    <button
                                        onClick={() => setShiftForm(f => ({
                                            ...f,
                                            saveAsDefault: !f.saveAsDefault,
                                        }))}
                                        className={`relative flex-shrink-0 w-10 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-green-400 ${
                                            shiftForm.saveAsDefault && !shiftForm.clearDefault
                                                ? 'bg-green-500' : 'bg-gray-200'
                                        }`}
                                        aria-pressed={shiftForm.saveAsDefault && !shiftForm.clearDefault}
                                        aria-label="Toggle recurring schedule">
                                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                                            shiftForm.saveAsDefault && !shiftForm.clearDefault ? 'translate-x-4' : 'translate-x-0'
                                        }`} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 bg-white">
                            <button onClick={closeModal} disabled={saving}
                                className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50">
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving || !hasChanges}
                                className="px-5 py-2 bg-[#A0826D] text-white rounded-lg text-sm font-medium hover:bg-[#8B6F5F] transition-colors disabled:opacity-60 flex items-center gap-2">
                                {saving && (
                                    <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="4" strokeOpacity="0.3"/>
                                        <path fill="white" d="M4 12a8 8 0 018-8v8z"/>
                                    </svg>
                                )}
                                {saving ? 'Saving…' : 'Save Schedule'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}